import Vue from 'vue'
import flagReviewService from 'services/api/flag-review'
import voteReviewService from 'services/api/vote-for-review'
import formatName from 'filters/format-name'
import globalStore from 'init/global-store'
import { hostsConfig } from 'init/config'
import logger from 'util/logger'
import Menu from 'objects/menu/menu'
import MenuList, { IMenuListItem } from 'objects/menu/menu-list'
import { RestError } from 'services/api-service'
import standalonePopoverLoader from 'widgets/standalone-popover/standalone-popover-loader'
import { MODERATION_URL, getSessionVarInt,
  setSessionVar, removeSessionVar } from 'util/turnto-utils'

import withRender from './review-toolbar.html'
import './review-toolbar.scss'

export const UPVOTED_SESSION_TYPE = 'rWasUpVoted'
export const DOWNVOTED_SESSION_TYPE = 'rWasDownVoted'

export default withRender(Vue.extend({
  data() {
    return {
      wasDownVoted: false,
      wasFlagged: false,
      wasUpVoted: false,
      store: globalStore,
      userSessionDownVote: this.downVotes,
      userSessionUpVote: this.upVotes,
      voteBeingProcessed: false
    }
  },
  props: {
    checkoutSiteKey: String,
    downVotes: { type: Number, required: true },
    review: { type: Object, required: true },
    upVotes: { type: Number, required: true }
  },
  components: {
    'tt-menu': Menu,
    'tt-menu-list': MenuList
  },
  computed: {
    downVoteCount() {
      return this.userSessionDownVote
    },
    downVoteIconName() {
      return this.wasDownVoted ? 'unhelpful-on' : 'unhelpful-off'
    },
    flagAriaLabel() {
      return this.$msg(this.wasFlagged ?
          'review.flagged.aria' : 'review.flag.aria', [this.username])
    },
    flagIconName() {
      return this.wasFlagged ? 'flag-on' : 'flag-off'
    },
    flagText() {
      return this.$msg(this.wasFlagged ? 'review.flagged' : 'review.flag')
    },
    isModerator() {
      return this.store.user && this.store.user.isModerator
    },
    moderationUrl() {
      // tslint:disable-next-line:max-line-length
      return `${MODERATION_URL}/reviews/rsolo?siteKey=${hostsConfig.siteKey}&id=${this.review.id}`
    },
    menuItems() {
      const flagText = this.$msg(this.wasFlagged ? 'review.flagged' : 'review.flag')
      const items = [
        { name: 'flag', iconName: this.flagIconName, title: flagText, disabled: this.wasFlagged }
      ] as Array<IMenuListItem>
      // TODO: check to see if is moderator
      if (this.isModerator) {
        items.push({ name: 'moderate', iconName: 'write', title: this.$msg('mediaModal.moderate') })
      }
      return items
    },
    upVoteCount() {
      return this.userSessionUpVote
    },
    upVoteIconName() {
      return this.wasUpVoted ? 'helpful-on' : 'helpful-off'
    },
    username() {
      return formatName(this.review.user)
    },
    wasVotedOn() {
      return this.wasDownVoted || this.wasUpVoted
    }
  },
  methods: {
    downVote(e) {
      const voteBtn = e.currentTarget
      if (this.wasDownVoted) {
        // was down voted, now the user is retracting that vote
        this.sendVote(0, -1, voteBtn)
      } else {
        // actual down vote
        // need to check if up vote already given by this user.  If so, need to retract
        const up = this.wasUpVoted ? -1 : 0
        this.sendVote(up, 1, voteBtn)
      }
    },
    flag() {
      if (this.wasFlagged) {
        return
      }
      this.wasFlagged = true
      flagReviewService.flagReview({
        id: this.review.id,
        checkoutSiteKey: this.checkoutSiteKey || '',
        sku: this.review.catItem.sku
      }).catch((e: RestError) => {
        this.wasFlagged = false
        let msgKey =  'unexpected.error'
        if (e.errorCode === 'turnto-offline') {
          msgKey = 'offline.error'
        } else {
          logger.warn('Error sending review flag', e)
        }
        standalonePopoverLoader.load({ message: this.$msg(msgKey), type: 'error' })
      })
    },
    menuItemSelected(item) {
      if (item.name === 'flag' && !this.wasFlagged) {
        this.flag()
      } else if (item.name === 'moderate') {
        this.moderate()
      }
    },
    removeUserDownVote() {
      if (getSessionVarInt(DOWNVOTED_SESSION_TYPE, this.review.id)) {
        this.userSessionDownVote -= 1
      }
      removeSessionVar(DOWNVOTED_SESSION_TYPE, this.review.id)
    },
    removeUserUpVote() {
      if (getSessionVarInt(UPVOTED_SESSION_TYPE, this.review.id)) {
        this.userSessionUpVote -= 1
      }
      removeSessionVar(UPVOTED_SESSION_TYPE, this.review.id)
    },
    sendVote(up: number, down: number, voteBtn) {
      this.voteBeingProcessed = true
      return voteReviewService.voteForReview({
        id: this.review.id,
        checkoutSiteKey: this.checkoutSiteKey || '',
        sku: this.review.catItem.sku
      }, {
        down,
        up
      }).then(() => {
        this.voteBeingProcessed = false
        this.wasUpVoted = up === 1 || (this.wasUpVoted && !(up === -1))
        if (this.wasUpVoted) {
          this.userSessionUpVote += 1
          setSessionVar(UPVOTED_SESSION_TYPE, this.review.id, this.userSessionUpVote)
          this.removeUserDownVote()
        } else {
          this.removeUserUpVote()
        }
        this.wasDownVoted = down === 1 || (this.wasDownVoted && !(down === -1))
        if (this.wasDownVoted) {
          this.userSessionDownVote += 1
          setSessionVar(DOWNVOTED_SESSION_TYPE, this.review.id, this.userSessionDownVote)
          this.removeUserUpVote()
        } else {
          this.removeUserDownVote()
        }
        this.$nextTick(() => voteBtn.focus())
      }).catch((e: RestError) => {
        this.voteBeingProcessed = false
        let msgKey =  'unexpected.error'
        if (e.errorCode === 'turnto-offline') {
          msgKey = 'offline.error'
        } else {
          logger.warn('Error sending vote', e)
        }
        standalonePopoverLoader.load({ message: this.$msg(msgKey), type: 'error' })
      })
    },
    upVote(e) {
      const voteBtn = e.currentTarget
      if (this.wasUpVoted) {
        // was up voted, now the user is retracting that vote
        this.sendVote(-1, 0, voteBtn)
      } else {
        // actual up vote
        // need to check if down vote already given by this user.  If so, need to retract
        const down = this.wasDownVoted ? -1 : 0
        this.sendVote(1, down, voteBtn)
      }
    }
  },
  created() {
    const rWasDownVoted = getSessionVarInt(DOWNVOTED_SESSION_TYPE, this.review.id)
    if (rWasDownVoted !== null) {
      this.wasDownVoted = true
      this.userSessionDownVote = rWasDownVoted
    }
    const rWasUpVoted = getSessionVarInt(UPVOTED_SESSION_TYPE, this.review.id)
    if (rWasUpVoted !== null) {
      this.wasUpVoted = true
      this.userSessionUpVote = rWasUpVoted
    }
  }
}))
